import { NotesModal } from '@/Components/Misc/NotesModal';
import { createContext, useContext, useState } from 'react';

const NotesContext = createContext();

export const NotesProvider = ({ children }) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [notesMode, setNotesMode] = useState('update');
  const [brandName, setBrandName] = useState('');
  const [existingNotes, setExistingNotes] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [clientId, setClientId] = useState(null);

  const MODE = {
    CREATE_FROM_SELECTION: 'create-from-selection',
    CREATE_FROM_EXISTING_NOTE: 'create-from-existing-note',
    CREATE_NEW_NOTE: 'create-new-note',
    LIST_NOTES: 'list_notes',
    EDIT: 'edit_single_note',
  };

  const loadExistingNotes = () => {
    const { clientId, briefId, brandId: extractedBrandId, hasRequiredIds } = getCurrentContextIds();

    setClientId(clientId);

    if (!hasRequiredIds || briefId === 'create') {
      return;
    }

    let notesEndpoint;

    if (briefId) {
      // If we have a brief_id, we can fetch notes for that brief directly
      // Using the endpoint format detected from the URL
      notesEndpoint = route('notes.getByBrief', { client: clientId, briefId: briefId });
    } else if (extractedBrandId) {
      // If we have a brand_id but no brief_id, fetch notes for the brand
      notesEndpoint = route('notes.getByBrand', { client: clientId, brandId: extractedBrandId })
    } else {
      // As a last resort, fetch all notes for this client
      notesEndpoint = route('notes.getByClient', { client: clientId });
    }

    fetch(notesEndpoint)
      .then(response => response.json())
      .then(data => {
        setBrandName(data.brand.name);
        setBrandId(data.brand.id);

        setExistingNotes(() => {
          return data.notes.map(note => {
            return {
              id: note.id,
              title: note.title,
              date: note.date,
              content: note.note
            }
          });
        });
      });
  }

  return (
    <NotesContext.Provider value={{
      showNotesModal,
      setShowNotesModal,
      selectedText,
      setSelectedText,
      notesMode,
      setNotesMode,
      brandName,
      setBrandName,
      existingNotes,
      setExistingNotes,
      MODE,
      brandId,
      setBrandId,
      loadExistingNotes,
      clientId,
      setClientId
    }}>
      {children}
      <NotesModal />
    </NotesContext.Provider>
  );
};

const useNotes = () => {
  const context = useContext(NotesContext);

  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }

  return context;
};

const extractIdFromUrl = (type) => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(segment => segment.length > 0);

  // Handle URL pattern like /clients/4/brief/81/edit
  if (type === 'client' && segments[0] === 'clients' && segments.length >= 2) {
    return segments[1];
  }

  // Handle both "brief" (singular) and "briefs" (plural) and "reports" patterns
  if (type === 'brief') {
    // Check for pattern: clients/{id}/brief/{brief_id}
    if (segments.length >= 4 && segments[0] === 'clients' && segments[2] === 'brief') {
      return segments[3];
    }

    // Check for pattern: clients/{id}/briefs/{brief_id}
    if (segments.length >= 4 && segments[0] === 'clients' && segments[2] === 'briefs') {
      return segments[3];
    }

    // Check for pattern: clients/{id}/reports/{report_id}
    // In this case, treat the report ID as the brief ID
    if (segments.length >= 4 && segments[0] === 'clients' && segments[2] === 'reports') {
      return segments[3];
    }
  }

  // Original fallback logic
  for (let i = 0; i < segments.length - 1; i++) {
    if (segments[i] === type + 's' || segments[i] === type) {
      const id = segments[i + 1];
      if (id && !isNaN(parseInt(id))) {
        return id;
      }
    }
  }

  // If we can't extract from URL path segments, try to find it in the page content
  // Many applications store entity IDs in data attributes or hidden inputs
  const dataElement = document.querySelector(`[data-${type}-id]`);
  if (dataElement) {
    const id = dataElement.getAttribute(`data-${type}-id`);
    return id;
  }

  return null;
}

const getCurrentContextIds = () => {
  // Extract from URL query parameters first
  const urlParams = new URL(window.location.href).searchParams;
  let clientId = urlParams.get('client_id');
  let briefId = urlParams.get('brief_id');
  let brandId = urlParams.get('brand_id');
  let reportId = urlParams.get('report_id');

  // If not in query params, extract from URL path
  if (!clientId) {
    clientId = extractIdFromUrl('client');
  }

  if (!briefId) {
    briefId = extractIdFromUrl('brief');
  }

  if (!brandId) {
    brandId = extractIdFromUrl('brand');
  }

  if (!briefId) {
    briefId = reportId;
  }

  // Check for data attributes on the page as a last resort
  if (!clientId) {
    const clientIdElement = document.querySelector('[data-client-id]');
    if (clientIdElement) {
      clientId = clientIdElement.getAttribute('data-client-id');
    }
  }

  if (!briefId) {
    const briefIdElement = document.querySelector('[data-brief-id]');
    if (briefIdElement) {
      briefId = briefIdElement.getAttribute('data-brief-id');
    }
  }

  const contextIds = {
    clientId,
    briefId,
    brandId,
    // Check if we have the minimum required IDs
    hasRequiredIds: (!!clientId && !!briefId) || !!brandId
  };

  return contextIds;
}

export { useNotes };
